import { AuthorityBadge } from '../components/AuthorityBadge';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { InteractiveInsightCard } from '../components/interactive/InteractiveInsightCard';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const METRIC_CARDS = [
  {
    value: '10x',
    label: 'Productivity Gain',
    sublabel: 'Task automation impact',
    color: 'blue',
    details: {
      explanation: 'AI digital twins amplify individual productivity by handling routine communications, scheduling, research synthesis, and document preparation. Studies show knowledge workers spend 60% of their time on coordination tasks that an AI twin can automate or accelerate.',
      example: 'A marketing manager\'s AI twin drafts social posts, summarizes competitor research, and prepares meeting briefs overnight, ready for review each morning.',
      relatedConcepts: ['Task Automation', 'Cognitive Offloading', 'Workflow Optimization'],
      references: [{ title: 'McKinsey (2023) - The Economic Potential of Generative AI' }],
      quiz: {
        question: 'What percentage of knowledge worker time is typically spent on coordination tasks?',
        options: ['20%', '40%', '60%', '80%'],
        correctIndex: 2,
        explanation: 'Research shows approximately 60% of knowledge worker time goes to coordination: emails, meetings, searching for information, and status updates.'
      }
    }
  },
  {
    value: 'NLP',
    label: 'Natural Language',
    sublabel: 'Human-like interaction',
    color: 'cyan',
    details: {
      explanation: 'Modern AI assistants use transformer-based language models to understand context, tone, and intent. Unlike rule-based chatbots, they can handle ambiguous requests, follow conversational threads, and adapt their communication style to match the user.',
      example: 'Say "Handle my morning routine" and the twin knows to check email, flag urgent items, summarize overnight messages, and prepare your daily briefing.',
      relatedConcepts: ['Transformer Architecture', 'Context Windows', 'Intent Classification'],
      references: [{ title: 'Vaswani et al. (2017) - Attention Is All You Need' }],
      quiz: {
        question: 'What architecture powers modern language AI?',
        options: ['Recurrent Neural Networks', 'Convolutional Networks', 'Transformer Architecture', 'Decision Trees'],
        correctIndex: 2,
        explanation: 'The transformer architecture, introduced in 2017, uses self-attention mechanisms to process language in parallel, enabling the powerful language models we use today.'
      }
    }
  },
  {
    value: '24/7',
    label: 'Always Available',
    sublabel: 'Continuous operation',
    color: 'emerald',
    details: {
      explanation: 'An AI assistant twin operates continuously, handling requests across time zones and outside business hours. It can process incoming communications, prepare materials for the next day, and respond to urgent queries when the human is unavailable.',
      example: 'A freelancer receives a client email at 2 AM. The twin acknowledges receipt, asks clarifying questions, and drafts a preliminary response for morning review.',
      relatedConcepts: ['Asynchronous Communication', 'Global Availability', 'Automated Triage'],
      references: [{ title: 'Deloitte - AI Augmented Workforce Report' }],
      quiz: {
        question: 'What is a key advantage of AI assistants over human assistants for global teams?',
        options: ['Lower cost', 'Time-zone independent availability', 'Better creativity', 'Faster typing'],
        correctIndex: 1,
        explanation: 'AI assistants operate across all time zones simultaneously, ensuring consistent responsiveness regardless of when or where requests originate.'
      }
    }
  },
  {
    value: 'E2E',
    label: 'Privacy by Design',
    sublabel: 'End-to-end encrypted',
    color: 'violet',
    details: {
      explanation: 'Personal AI twins handle sensitive information: emails, documents, schedules, and communications. Privacy-first architecture ensures data stays under user control with end-to-end encryption, local processing options, and zero-knowledge principles.',
      example: 'Your twin processes your email locally, never sending raw content to external servers. Summaries and actions are generated on-device or in your private cloud.',
      relatedConcepts: ['Zero-Knowledge Architecture', 'Local Processing', 'Data Sovereignty'],
      references: [{ title: 'NIST Privacy Framework (2023)' }],
      quiz: {
        question: 'What does "zero-knowledge" mean in AI privacy?',
        options: ['The AI knows nothing', 'The service provider cannot access user data', 'No data is stored', 'The AI has no training data'],
        correctIndex: 1,
        explanation: 'Zero-knowledge architecture means the service provider has no ability to access or read user data, even if compelled, because encryption keys are held only by the user.'
      }
    }
  }
];

const FEATURE_CARDS = [
  {
    title: 'Style Mirroring',
    color: 'blue',
    description: 'Your twin learns your writing voice, vocabulary preferences, and communication patterns to draft messages indistinguishable from your own.',
    details: {
      explanation: 'Style mirroring uses fine-tuning and few-shot learning to capture individual communication patterns. The system analyzes your emails, messages, and documents to build a style profile covering tone (formal/casual), sentence structure, vocabulary frequency, and domain-specific terminology.',
      example: 'After analyzing 500 of your emails, the twin matches your greeting style ("Hey team" vs "Dear colleagues"), sign-off pattern, and tendency to use bullet points vs paragraphs.',
      relatedConcepts: ['Style Transfer', 'Few-Shot Learning', 'Persona Modeling'],
      references: [{ title: 'Jhamtani et al. (2017) - Shakespearizing Modern English' }],
      quiz: {
        question: 'How does an AI learn your writing style?',
        options: ['Copying your templates', 'Analyzing patterns in your existing communications', 'Asking you questions about preferences', 'Using a standard professional tone'],
        correctIndex: 1,
        explanation: 'Style mirroring analyzes patterns across your existing communications: vocabulary, tone, structure, and formatting preferences to build a personalized style model.'
      }
    }
  },
  {
    title: 'Context Awareness',
    color: 'cyan',
    description: 'Maintains awareness of your projects, relationships, and priorities to provide contextually relevant assistance without repeated explanations.',
    details: {
      explanation: 'Context awareness goes beyond simple chat memory. The twin maintains a structured knowledge graph of your projects, contacts, deadlines, and preferences. It understands that "the Johnson proposal" refers to a specific document, and "send it to Sarah" means your colleague Sarah Chen.',
      example: 'You say "Prepare for tomorrow\'s board meeting." The twin knows which meeting, pulls relevant docs, checks for pending action items, and drafts talking points based on the agenda.',
      relatedConcepts: ['Knowledge Graphs', 'Entity Resolution', 'Long-Term Memory'],
      references: [{ title: 'Retrieval Augmented Generation (RAG) Architecture Patterns' }],
    }
  },
  {
    title: 'Multi-Platform Integration',
    color: 'emerald',
    description: 'Connects across email, calendar, documents, messaging, and project management tools to act as a unified interface for your digital life.',
    details: {
      explanation: 'Modern knowledge workers use 10+ apps daily. The AI twin integrates across these platforms, understanding that a Slack message about a deadline relates to a Jira ticket, which connects to a Google Doc, which needs input from a person in your Outlook contacts.',
      example: 'A client emails requesting a project update. The twin checks Jira for status, pulls the latest document from Drive, and drafts a comprehensive response with attachments.',
      relatedConcepts: ['API Integration', 'Unified Inbox', 'Cross-Platform Orchestration'],
      references: [{ title: 'MuleSoft Connectivity Benchmark Report' }],
    }
  },
  {
    title: 'Adaptive Learning',
    color: 'violet',
    description: 'Continuously improves from your feedback and corrections, becoming more accurate and aligned with your preferences over time.',
    details: {
      explanation: 'Every interaction is a learning opportunity. When you edit a draft, the twin analyzes what you changed and why. Explicit feedback ("more formal next time") and implicit signals (which suggestions you accept vs reject) continuously refine the model.',
      example: 'You consistently shorten the twin\'s email drafts. After 20 corrections, it automatically generates more concise messages matching your preferred length.',
      relatedConcepts: ['Reinforcement Learning from Human Feedback', 'Preference Optimization', 'Active Learning'],
      references: [{ title: 'Ouyang et al. (2022) - Training Language Models to Follow Instructions' }],
    }
  }
];

const HERITAGE_CARDS = [
  {
    title: 'From Personal Secretaries',
    color: 'amber',
    description: 'For centuries, executives relied on personal secretaries who learned their preferences, managed correspondence, and anticipated needs through years of close working relationships.',
    details: {
      explanation: 'The personal secretary role, dating back to Renaissance-era courts, evolved into the modern executive assistant. These professionals developed deep understanding of their principals: communication preferences, decision patterns, relationship dynamics. AI twins digitize this centuries-old practice.',
      example: 'A Victorian-era personal secretary would draft letters in their employer\'s voice, manage their social calendar, and handle correspondence. Today\'s AI twin does the same across digital channels.',
      relatedConcepts: ['Executive Assistance', 'Institutional Knowledge', 'Personal Knowledge Management'],
      references: [{ title: 'History of Office Work and Administrative Professionals' }],
    }
  },
  {
    title: 'Expert Systems Era',
    color: 'emerald',
    description: 'The 1980s expert systems movement first attempted to capture human expertise in software. MYCIN, XCON, and others showed both the promise and limitations of rule-based knowledge encoding.',
    details: {
      explanation: 'Expert systems encoded human decision-making as if-then rules. MYCIN diagnosed bacterial infections. XCON configured computer orders. These systems proved that capturing expertise was valuable but brittle. Modern AI twins learn from data rather than hand-coded rules, making them far more flexible.',
      example: 'MYCIN used ~600 rules to diagnose infections. A modern AI twin learns from millions of examples, handling ambiguity and novel situations that rule-based systems could not.',
      relatedConcepts: ['Rule-Based Systems', 'Knowledge Engineering', 'Knowledge Representation'],
      references: [{ title: 'Buchanan & Shortliffe (1984) - Rule-Based Expert Systems' }],
    }
  },
  {
    title: 'The LLM Revolution',
    color: 'cyan',
    description: 'Large language models transformed AI assistance from scripted interactions to natural conversation, enabling AI twins that truly understand and generate human-quality communication.',
    details: {
      explanation: 'GPT, Claude, and other LLMs represent a paradigm shift. Instead of encoding rules, they learn language patterns from vast text corpora. This enables understanding of context, nuance, humor, and domain-specific jargon that was impossible with previous approaches. The AI assistant twin builds on this foundation.',
      example: 'Pre-LLM: "Set reminder for 3pm." Post-LLM: "Remind me about the thing Sarah mentioned yesterday" - the twin resolves the reference and creates the right reminder.',
      relatedConcepts: ['Large Language Models', 'In-Context Learning', 'Emergent Capabilities'],
      references: [{ title: 'Brown et al. (2020) - Language Models are Few-Shot Learners' }],
    }
  }
];

const Home = () => {
  return (
    <div className="container-custom py-12 sm:py-16 lg:py-20">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-[-20%] left-[20%] w-[500px] h-[500px] rounded-full bg-primary-500/5 blur-3xl" />
          <div className="absolute top-[10%] right-[10%] w-[400px] h-[400px] rounded-full bg-accent-500/5 blur-3xl" />
          <div className="absolute bottom-[-10%] left-[40%] w-[350px] h-[350px] rounded-full bg-primary-500/5 blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          <div className="inline-block mb-6 px-3 py-1 rounded-full border border-primary-400/30 bg-primary-500/5">
            <span className="text-xs font-medium text-primary-400">
              AI-Powered Personal Productivity
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight">
            <span className="gradient-text">AssistantTwin</span> -
            <br className="hidden sm:block" /> Your AI That Knows You
          </h1>

          <p className="text-base sm:text-lg text-surface-400 mb-4 max-w-2xl mx-auto leading-relaxed">
            A personal AI companion that learns your communication style, understands your context,
            and handles tasks the way you would. Privacy-first, always available, continuously improving.
          </p>

          <p className="text-sm text-surface-500 mb-8 sm:mb-10">
            Built on advances in style transfer, retrieval-augmented generation, and adaptive
            learning - AssistantTwin represents the next evolution of personal AI assistance.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/demo"
              className="btn-primary text-base sm:text-lg px-8 py-4 inline-block rounded-lg w-full sm:w-auto min-h-[44px]"
            >
              Try Interactive Demo
            </Link>
          </div>
        </motion.div>
      </div>

      <div className="w-24 h-px mx-auto mt-10 mb-2 bg-gradient-to-r from-transparent via-primary-400/40 to-transparent" />

      <AuthorityBadge
        lastUpdated="February 2026"
        researchInitiative="AssistantTwin AI Research"
        sourceCount={10}
        domain="assistanttwin.com"
      />

      <motion.div id="features"
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.2 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-12 sm:mt-16 max-w-4xl mx-auto"
      >
        {METRIC_CARDS.map((item) => (
          <InteractiveInsightCard
            key={item.label}
            title={item.label}
            value={item.value}
            label={item.label}
            sublabel={item.sublabel}
            color={item.color}
            details={item.details}
            className="text-center"
          />
        ))}
      </motion.div>

      <motion.section id="definition"
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-16 sm:mt-20"
      >
        <div className="card p-6 border-primary-400/20 bg-surface-900/50 max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-surface-50 mb-4 flex items-center gap-2">
            What is an AI Assistant Twin?
          </h2>
          <p className="text-sm text-surface-400 mb-4">
            An AI assistant twin is a personalized digital companion that mirrors your communication
            style, understands your context, and handles tasks the way you would. Unlike generic
            chatbots, it learns from your interactions to become increasingly aligned with your
            preferences, priorities, and working patterns.
          </p>
          <p className="text-sm text-surface-400">
            Think of it as a digital extension of yourself: it drafts emails in your voice,
            manages your schedule with your priorities in mind, and synthesizes information
            the way you prefer to consume it. The more you use it, the better it becomes.
          </p>
        </div>
      </motion.section>

      <motion.section
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.35 }}
        className="mt-16 sm:mt-20"
      >
        <div className="card p-6 border-accent-400/20 bg-surface-900/50 max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-surface-50 mb-4 flex items-center gap-2">
            Core Capabilities
          </h2>
          <p className="text-sm text-surface-400 mb-6">
            Four pillars of truly personalized AI assistance.
            <span className="text-accent-400 ml-1">Click any capability to learn more and test your knowledge.</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {FEATURE_CARDS.map((feature) => (
              <InteractiveInsightCard
                key={feature.title}
                title={feature.title}
                description={feature.description}
                color={feature.color}
                details={feature.details}
              />
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-16 sm:mt-20"
      >
        <div className="card p-6 border-amber-400/20 bg-surface-900/50 max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-surface-50 mb-4 flex items-center gap-2">
            Evolution of Personal AI Assistance
          </h2>
          <p className="text-sm text-surface-400 mb-6">
            From human secretaries to expert systems to modern AI twins.
            <span className="text-amber-400 ml-1">Click to explore each era.</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {HERITAGE_CARDS.map((heritage) => (
              <InteractiveInsightCard
                key={heritage.title}
                title={heritage.title}
                description={heritage.description}
                color={heritage.color}
                details={heritage.details}
              />
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section id="applications"
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.45 }}
        className="mt-16 sm:mt-20"
      >
        <div className="card p-6 border-emerald-400/20 bg-surface-900/50 max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-surface-50 mb-4 flex items-center gap-2">
            The Privacy-First Advantage
          </h2>
          <div className="space-y-4 text-sm text-surface-400">
            <p>
              <strong className="text-emerald-400">Your Data, Your Control:</strong> AssistantTwin
              processes data locally or in your private cloud. Your emails, documents, and messages
              never leave your security perimeter. Zero-knowledge architecture means even the
              service provider cannot access your information.
            </p>
            <p>
              Unlike cloud-first assistants that send everything to remote servers, AssistantTwin
              can run entirely on-device for maximum privacy. This makes it suitable for lawyers,
              doctors, financial advisors, and anyone handling sensitive client information.
            </p>
            <p>
              The twin learns from your feedback without transmitting raw data. Style profiles,
              preferences, and context models are encrypted and stored under your control, with
              the ability to export or delete at any time.
            </p>
            <p>
              <strong className="text-emerald-400">AssistantTwin's Mission:</strong> To make
              truly personalized AI assistance accessible to everyone while maintaining the highest
              standards of privacy and data sovereignty.
            </p>
          </div>
        </div>
      </motion.section>

      <motion.div
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.5 }}
        className="mt-16 sm:mt-20 max-w-4xl mx-auto text-center"
      >
        <div className="card p-6 border-primary-400/30 bg-gradient-to-br from-primary-950/20 to-surface-900">
          <h3 className="text-xl font-bold text-primary-400 mb-3">Experience the Demo</h3>
          <p className="text-sm text-surface-300 mb-4">
            Walk through an interactive demonstration of style mirroring, context awareness,
            and adaptive learning. See how AssistantTwin personalizes to your needs.
          </p>
          <Link
            to="/demo"
            className="btn-primary text-sm sm:text-base px-6 py-3 inline-block rounded-lg w-full sm:w-auto min-h-[44px]"
          >
            Open the Interactive Demo
          </Link>
        </div>
      </motion.div>

      <motion.section
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.55 }}
        className="mt-16 sm:mt-20 max-w-4xl mx-auto"
      >
        <div className="card p-6 border-surface-600/20 bg-surface-900/30">
          <h3 className="text-lg font-bold text-surface-300 mb-4">Selected References</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 text-xs text-surface-500">
            <div>
              <p className="text-surface-400 font-medium">Language Models</p>
              <p>Vaswani et al. <em>Attention Is All You Need</em> (2017)</p>
              <p>Brown et al. <em>Language Models are Few-Shot Learners</em> (2020)</p>
            </div>
            <div>
              <p className="text-surface-400 font-medium">Personalization</p>
              <p>Ouyang et al. <em>Training LMs to Follow Instructions</em> (2022)</p>
              <p>Jhamtani et al. <em>Style Transfer in Text</em> (2017)</p>
            </div>
            <div>
              <p className="text-surface-400 font-medium">Privacy & Security</p>
              <p>NIST Privacy Framework (2023)</p>
              <p>Zero-Knowledge Architecture Patterns</p>
            </div>
            <div>
              <p className="text-surface-400 font-medium">Productivity Research</p>
              <p>McKinsey. <em>Economic Potential of Generative AI</em> (2023)</p>
              <p>Deloitte. <em>AI Augmented Workforce Report</em></p>
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="mt-16 sm:mt-20 max-w-4xl mx-auto"
      >
        <div className="card p-6 border-surface-600/20 bg-surface-900/30 rounded-xl">
          <h3 className="text-lg font-bold text-surface-300 mb-2">Related Topics in the Knowledge Network</h3>
          <p className="text-sm text-surface-400 mb-4">Explore related subjects across the Global Knowledge Graph Network.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <a
              href="https://mentortwin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 rounded-xl border border-surface-200 dark:border-surface-700 hover:border-primary-500/50 transition-all duration-300 hover:shadow-md"
            >
              <h4 className="font-semibold text-surface-900 dark:text-surface-50 mb-1">Mentor Twin</h4>
              <p className="text-sm text-surface-500 dark:text-surface-400">AI mentorship and guided learning</p>
            </a>
            <a
              href="https://jewtwin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 rounded-xl border border-surface-200 dark:border-surface-700 hover:border-primary-500/50 transition-all duration-300 hover:shadow-md"
            >
              <h4 className="font-semibold text-surface-900 dark:text-surface-50 mb-1">JewTwin</h4>
              <p className="text-sm text-surface-500 dark:text-surface-400">Jewish cultural heritage AI</p>
            </a>
            <a
              href="https://digital-twin-pro.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 rounded-xl border border-surface-200 dark:border-surface-700 hover:border-primary-500/50 transition-all duration-300 hover:shadow-md"
            >
              <h4 className="font-semibold text-surface-900 dark:text-surface-50 mb-1">Digital Twin Pro</h4>
              <p className="text-sm text-surface-500 dark:text-surface-400">Professional digital twin platform</p>
            </a>
          </div>
          <p className="text-xs text-surface-500 mt-4 text-center">
            <a href="https://globalknowledgegraph.com" className="text-primary-400 hover:underline transition-colors duration-300">
              Part of the Global Knowledge Graph Network
            </a>
          </p>
        </div>
      </motion.section>
    </div>
  );
};

export default Home;
